import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  Textarea,
  Button,
  Field,
  IconButton,
} from '@chakra-ui/react';
import { MdMic as MicIcon, MdStop as StopIcon } from 'react-icons/md';
import { RecipeClient } from '@/lib/recipeClient';
import { toaster } from '@/lib/toaster';
import { validateRecipeText } from '@/utils/importUtils';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { useImportSheet } from '@/contexts/ImportSheetContext';

export default function ImportTextPanel() {
  const { startPolling, isPolling, closeExistingToast } = useImportSheet();
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { isRecording, startRecording, stopRecording, cancelRecording } = useVoiceInput({
    onTranscriptionComplete: (transcribedText) => {
      setText((prev) => {
        const separator = prev.trim() ? ' ' : '';
        return prev + separator + transcribedText;
      });
    },
  });

  useEffect(() => {
    return () => {
      if (isRecording) cancelRecording();
    };
  }, [isRecording, cancelRecording]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateRecipeText(text);
    if (!validation.isValid) {
      setError(validation.error!);
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      closeExistingToast();
      const response = await RecipeClient.importFromText({ text: text.trim() });
      startPolling(response.jobId);
      setIsSubmitting(false);
    } catch {
      closeExistingToast();
      setIsSubmitting(false);
      setError('Failed to submit import. Please check your text and try again.');
      toaster.create({
        title: 'Error submitting import',
        description: 'Please check your text and try again',
        type: 'error',
      });
    }
  };

  const isProcessing = isSubmitting || isPolling;

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={4} align="stretch">
        <Field.Root invalid={!!error}>
          <Box position="relative" w="100%">
            <Textarea
              ref={textAreaRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError('');
              }}
              placeholder={`Paste your recipe here, or tap the mic to dictate…

For example:
Chocolate Chip Cookies
Serves 24 (include "Serves X" for nutrition per serving)

Ingredients:
- 2 cups flour
- 1 cup butter
- 1 cup chocolate chips

Instructions:
1. Mix dry ingredients
2. Add butter
3. Bake at 180°C for 12 minutes`}
              aria-label="Recipe text"
              size="md"
              bg="bg.surface"
              borderColor="accent.secondaryBorder"
              _hover={{ borderColor: 'accent.secondary' }}
              _focus={{
                borderColor: 'accent.secondary',
                boxShadow: '0 0 0 1px var(--chakra-colors-accent-secondary)',
                bg: 'bg.surface',
              }}
              disabled={isProcessing || isRecording}
              minH="200px"
              resize="vertical"
              pr="44px"
              fontFamily="body"
              fontSize="sm"
            />
            <Box position="absolute" bottom="10px" right="10px">
              <IconButton
                aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
                size="sm"
                variant={isRecording ? 'solid' : 'ghost'}
                bg={isRecording ? 'status.error' : 'transparent'}
                color={isRecording ? 'white' : 'accent.secondary'}
                _hover={isRecording ? { bg: 'red.600' } : { bg: 'accent.secondarySubtle' }}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
              >
                {isRecording ? <StopIcon /> : <MicIcon />}
              </IconButton>
            </Box>
          </Box>
          {error && <Field.ErrorText>{error}</Field.ErrorText>}
        </Field.Root>

        {isRecording && (
          <Text fontSize="xs" color="accent.secondary" fontWeight="500" textAlign="center" fontFamily="body">
            Listening… tap stop when done
          </Text>
        )}

        <Button
          type="submit"
          bg="accent.emphasis"
          color="white"
          _hover={{ bg: 'accent.emphasisHover' }}
          size="md"
          w="100%"
          loading={isProcessing}
          loadingText="Processing…"
          disabled={!text.trim() || isRecording}
        >
          Import recipe
        </Button>

        <Text fontSize="xs" fontWeight="300" color="text.muted" textAlign="center" fontFamily="body">
          Paste or dictate any recipe text — our AI extracts ingredients, instructions, and times automatically.
        </Text>
      </VStack>
    </form>
  );
}
